// src/employee/pages/EmployeeTasks.js
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCalendarAlt, FaPlus, FaTrash, FaCheckCircle, FaRegCircle,
  FaFileUpload, FaSave, FaTrophy, FaChevronRight, FaEdit, FaChevronLeft, FaArrowLeft
} from "react-icons/fa";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import {
  doc, setDoc, onSnapshot, serverTimestamp, updateDoc, arrayUnion,
  query, collection, where, orderBy
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";
import { format, parseISO } from "date-fns";

export default function EmployeeTasks() {
  const { user } = useEmployeeAuth();
  const { stats } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState("list"); // 'list', 'detail', 'edit'
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [diary, setDiary] = useState({
    notes: "",
    tasks: [],
    attachments: [],
    hrFeedback: "",
    hrRating: 0
  });
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [newTask, setNewTask] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Handle URL Params
  useEffect(() => {
    const dateParam = searchParams.get("date");
    if (dateParam) {
      setSelectedDate(dateParam);
      setViewMode("detail");
    } else {
      setViewMode("list");
    }
  }, [searchParams]);

  // Fetch diary for selected date
  useEffect(() => {
    if (!user?.email || !selectedDate || viewMode === "list") return;

    const diaryId = `${user.email}_${selectedDate}`;
    const unsub = onSnapshot(doc(db, "dailyDiaries", diaryId), (docSnap) => {
      if (docSnap.exists()) {
        setDiary(docSnap.data());
      } else {
        setDiary({
          notes: "",
          tasks: [],
          attachments: [],
          hrFeedback: "",
          hrRating: 0
        });
      }
    });

    return () => unsub();
  }, [user?.email, selectedDate, viewMode]);

  // Fetch all history/list
  useEffect(() => {
    if (!user?.email) return;
    setHistoryLoading(true);
    const q = query(
      collection(db, "dailyDiaries"),
      where("employeeEmail", "==", user.email),
      orderBy("date", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setHistoryEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setHistoryLoading(false);
    }, (err) => {
      console.error("History fetch error", err);
      setHistoryLoading(false);
    });
    return () => unsub();
  }, [user?.email]);

  const handleSave = async () => {
    if (!user?.email || !selectedDate) return;
    setSaving(true);
    try {
      const diaryId = `${user.email}_${selectedDate}`;
      await setDoc(doc(db, "dailyDiaries", diaryId), {
        ...diary,
        employeeEmail: user.email,
        employeeName: user.name || user.email.split("@")[0],
        department: user.department || "General",
        date: selectedDate,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setViewMode("detail");
    } catch (err) {
      console.error("Save error", err);
      alert("Failed to save diary");
    } finally {
      setSaving(false);
    }
  };

  const addTask = () => {
    if (!newTask.trim()) return;
    setDiary(prev => ({
      ...prev,
      tasks: [...prev.tasks, { text: newTask.trim(), completed: false, id: Date.now() }]
    }));
    setNewTask("");
  };

  const toggleTask = (taskId) => {
    if (viewMode !== "edit") return;
    setDiary(prev => ({
      ...prev,
      tasks: prev.tasks.map(t => t.id === taskId ? { ...t, completed: !t.completed } : t)
    }));
  };

  const removeTask = (taskId) => {
    setDiary(prev => ({
      ...prev,
      tasks: prev.tasks.filter(t => t.id !== taskId)
    }));
  };

  const handleUpload = async (file) => {
    if (!user?.email || !selectedDate) return;
    try {
      setUploading(true);
      const diaryId = `${user.email}_${selectedDate}`;
      const fileRef = ref(storage, `diaries/${diaryId}/${file.name}`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      const newAttachment = { name: file.name, url, type: file.type };
      setDiary(prev => ({
        ...prev,
        attachments: [...(prev.attachments || []), newAttachment]
      }));

      await updateDoc(doc(db, "dailyDiaries", diaryId), {
        attachments: arrayUnion(newAttachment),
        updatedAt: serverTimestamp(),
      });
      alert("File uploaded successfully");
    } catch (err) {
      console.error("Upload error", err);
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleEntryClick = (date) => {
    setSearchParams({ date });
  };

  const startNewEntry = () => {
    setSelectedDate(format(new Date(), "yyyy-MM-dd"));
    setDiary({
      notes: "",
      tasks: [],
      attachments: [],
      hrFeedback: "",
      hrRating: 0
    });
    setViewMode("edit");
  };

  const renderListView = () => (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-black text-white">Diary Timeline</h2>
          <p className="text-white/40 text-sm">Review your daily progress and feedback.</p>
        </div>
        <button
          onClick={startNewEntry}
          className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-2xl font-bold hover:scale-105 transition shadow-xl flex items-center gap-2"
        >
          <FaPlus /> Add Todays Note
        </button>
      </div>

      <div className="grid gap-4">
        {historyLoading ? (
          <div className="text-center py-20 bg-white/5 rounded-3xl border border-white/10">
            <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white/30 font-bold uppercase tracking-widest text-xs">Loading Timeline...</p>
          </div>
        ) : historyEntries.length === 0 ? (
          <div className="text-center py-20 bg-white/5 rounded-3xl border border-white/10">
            <p className="text-white/30">No diary entries yet. Start by adding your first note!</p>
          </div>
        ) : (
          historyEntries.map((entry, i) => (
            <motion.div
              key={entry.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => handleEntryClick(entry.date)}
              className="group cursor-pointer bg-white/5 border border-white/10 p-6 rounded-3xl flex flex-col md:flex-row md:items-center justify-between hover:bg-white/10 transition shadow-lg"
            >
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-white/10 flex flex-col items-center justify-center font-bold text-white/40 ring-1 ring-white/10 group-hover:ring-cyan-500/50 transition">
                  <span className="text-xs">{format(parseISO(entry.date), "MMM")}</span>
                  <span className="text-2xl leading-tight">{format(parseISO(entry.date), "dd")}</span>
                </div>
                <div>
                  <h4 className="font-bold text-white text-lg group-hover:text-cyan-400 transition">
                    {entry.notes ? (entry.notes.length > 80 ? entry.notes.slice(0, 80) + "..." : entry.notes) : "Documentation provided."}
                  </h4>
                  <div className="flex gap-4 mt-1 text-[10px] text-white/30 uppercase tracking-widest font-bold">
                    <span className="flex items-center gap-1"><FaCheckCircle className="text-cyan-500/50" /> {entry.tasks?.length || 0} Tasks</span>
                    {entry.attachments?.length > 0 && <span className="flex items-center gap-1"><FaFileUpload className="text-purple-500/50" /> {entry.attachments.length} Files</span>}
                  </div>
                </div>
              </div>
              <div className="mt-4 md:mt-0 flex items-center gap-4">
                {entry.hrFeedback && (
                  <div className="flex items-center gap-2 bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20">
                    <span className="text-[10px] font-black text-emerald-400 uppercase">Review Recieved</span>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map(s => <FaTrophy key={s} size={8} className={s <= entry.hrRating ? "text-yellow-400" : "text-white/10"} />)}
                    </div>
                  </div>
                )}
                <div className="text-white/20 group-hover:text-cyan-400 transform group-hover:translate-x-2 transition">
                  <FaChevronRight />
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );

  const renderDetailView = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <button
          onClick={() => navigate("/employee/dashboard/tasks")}
          className="text-white/40 hover:text-white transition flex items-center gap-2 text-sm font-bold"
        >
          <FaArrowLeft /> Back to Timeline
        </button>
        <div className="flex items-center gap-4">
          <div className="bg-white/10 px-4 py-2 rounded-xl border border-white/20 text-white font-bold flex items-center gap-2">
            <FaCalendarAlt className="text-cyan-400" /> {format(parseISO(selectedDate), "EEEE, d MMMM yyyy")}
          </div>
          <button
            onClick={() => setViewMode("edit")}
            className="bg-white/10 hover:bg-white/20 text-white px-6 py-2 rounded-xl font-bold transition flex items-center gap-2 border border-white/20"
          >
            <FaEdit /> Edit Entry
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="backdrop-blur-3xl bg-white/5 border border-white/10 rounded-3xl p-8 space-y-6 shadow-2xl min-h-[400px]">
            <label className="text-sm font-bold text-cyan-400 uppercase tracking-wider block">Daily Documentation</label>
            <div className="text-white/80 leading-relaxed whitespace-pre-wrap text-lg">
              {diary.notes || "No notes written for this day."}
            </div>

            {diary.attachments?.length > 0 && (
              <div className="pt-8 border-t border-white/10">
                <label className="text-xs font-bold text-white/30 uppercase block mb-4">Supporting Documents</label>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {diary.attachments.map((file, i) => (
                    <a key={i} href={file.url} target="_blank" rel="noreferrer" className="group relative bg-white/5 border border-white/10 rounded-2xl p-4 hover:bg-white/10 transition overflow-hidden">
                      <FaFileUpload className="text-cyan-400 mb-2" />
                      <div className="text-[10px] text-white/60 truncate font-bold">{file.name}</div>
                      <div className="text-cyan-400 text-[10px] font-black uppercase mt-1">Download</div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {diary.hrFeedback && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-emerald-500/10 border border-emerald-500/30 rounded-3xl p-8 relative">
              <div className="absolute top-8 right-8 flex gap-1">
                {[1, 2, 3, 4, 5].map(s => <FaTrophy key={s} className={s <= diary.hrRating ? "text-yellow-400" : "text-white/10"} />)}
              </div>
              <h3 className="text-emerald-400 font-black mb-4 flex items-center gap-2 uppercase tracking-widest text-sm">
                <FaCheckCircle /> Official Feedback
              </h3>
              <p className="text-white text-xl italic font-serif leading-relaxed">"{diary.hrFeedback}"</p>
              <div className="mt-4 text-white/30 text-[10px] font-bold uppercase tracking-widest">Reviewed by HR Admin</div>
            </motion.div>
          )}
        </div>

        <div className="space-y-6">
          <div className="backdrop-blur-3xl bg-white/5 border border-white/10 rounded-3xl p-6 space-y-4 shadow-2xl">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <FaCheckCircle className="text-cyan-400" /> Goals Accomplished
            </h3>
            <div className="space-y-3">
              {diary.tasks?.map((task) => (
                <div key={task.id} className="flex items-center gap-3 bg-white/5 p-4 rounded-2xl border border-white/5">
                  {task.completed ? <FaCheckCircle className="text-green-400" /> : <FaRegCircle className="text-white/20" />}
                  <span className={`text-sm ${task.completed ? "text-white/30 line-through" : "text-white/90"}`}>
                    {task.text}
                  </span>
                </div>
              ))}
              {(!diary.tasks || diary.tasks.length === 0) && <p className="text-center text-white/20 text-xs py-4">No specific goals logged.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEditView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <button
          onClick={() => setViewMode("detail")}
          className="text-white/40 hover:text-white transition flex items-center gap-2 text-sm font-bold"
        >
          <FaChevronLeft /> Cancel Editing
        </button>
        <div className="bg-white/10 px-4 py-2 rounded-xl border border-white/20 text-white font-bold flex items-center gap-2">
          Editing: {format(parseISO(selectedDate), "MMM d, yyyy")}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="backdrop-blur-3xl bg-white/5 border border-white/10 rounded-3xl p-8 space-y-6 shadow-2xl">
            <div className="flex flex-col md:flex-row gap-6 mb-4">
              <div className="flex-1">
                <label className="text-xs font-black text-cyan-400 uppercase tracking-widest mb-2 block">Entry Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white font-bold outline-none focus:ring-2 focus:ring-cyan-500/50"
                />
              </div>
              <div className="flex-1 opacity-50 cursor-not-allowed">
                <label className="text-xs font-black text-white/40 uppercase tracking-widest mb-2 block">Status</label>
                <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white/40 font-bold">
                  Drafting Documentation
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs font-black text-cyan-400 uppercase tracking-widest mb-2 block">Detailed Log</label>
              <textarea
                placeholder="List your branch names, commits, or general work notes..."
                className="w-full bg-white/5 border border-white/10 rounded-2xl p-6 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 min-h-[350px] resize-none transition-all text-lg"
                value={diary.notes}
                onChange={(e) => setDiary(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
            <div className="flex justify-between items-center pt-4 border-t border-white/10">
              <label className="flex items-center gap-2 cursor-pointer bg-white/10 hover:bg-white/20 text-white px-5 py-3 rounded-2xl transition shadow-lg border border-white/10">
                <FaFileUpload className="text-cyan-400" />
                <span className="text-sm font-bold">{uploading ? "Uploading..." : "Attach File"}</span>
                <input type="file" className="hidden" onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0])} />
              </label>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-10 py-4 rounded-2xl font-bold hover:scale-105 active:scale-95 transition shadow-xl disabled:opacity-50"
              >
                {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FaSave />}
                Publish Update
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="backdrop-blur-3xl bg-white/5 border border-white/10 rounded-3xl p-6 space-y-4 shadow-2xl">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <FaCheckCircle className="text-cyan-400" /> Daily Check-list
            </h3>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="New task..."
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:ring-1 focus:ring-cyan-500"
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && addTask()}
              />
              <button onClick={addTask} className="bg-cyan-500 text-white p-2 rounded-xl hover:scale-110 transition">
                <FaPlus />
              </button>
            </div>
            <div className="space-y-2">
              {diary.tasks?.map((task) => (
                <div key={task.id} className="group flex items-center justify-between bg-white/5 p-3 rounded-xl border border-white/5">
                  <div className="flex items-center gap-3 cursor-pointer flex-1" onClick={() => toggleTask(task.id)}>
                    {task.completed ? <FaCheckCircle className="text-green-400" /> : <FaRegCircle className="text-white/30" />}
                    <span className={`text-sm ${task.completed ? "text-white/30 line-through" : "text-white/90"}`}>
                      {task.text}
                    </span>
                  </div>
                  <button onClick={() => removeTask(task.id)} className="text-white/0 group-hover:text-red-400 transition ml-2">
                    <FaTrash size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-950 p-6 md:p-10 text-white">
      <div className="max-w-6xl mx-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={viewMode + (searchParams.get("date") || "")}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            {viewMode === "list" ? renderListView() :
              viewMode === "edit" ? renderEditView() : renderDetailView()}
          </motion.div>
        </AnimatePresence>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.05); }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.2); border-radius: 10px; }
      `}</style>
    </div>
  );
}